import { InvestmentFolio, InvestmentSummary, FamilyMemberSummary, FamilyMember, InvestmentType } from '../types';

export const calculateInvestmentSummary = (investments: InvestmentFolio[]): InvestmentSummary => {
  const totalInvested = investments.reduce((sum, investment) => sum + investment.investedAmount, 0);
  const totalCurrentValue = investments.reduce((sum, investment) => sum + investment.currentValue, 0);
  const totalGrowth = totalCurrentValue - totalInvested;
  const growthPercentage = totalInvested > 0 ? (totalGrowth / totalInvested) * 100 : 0;

  return {
    totalInvested,
    totalCurrentValue,
    totalGrowth,
    growthPercentage,
  };
};

export const calculateFamilyMemberSummary = (
  familyMember: FamilyMember,
  investments: InvestmentFolio[]
): FamilyMemberSummary => {
  const memberId = familyMember._id || familyMember.id;
  const memberInvestments = investments.filter(inv => {
    const invMemberId = typeof inv.familyMemberId === 'string' 
      ? inv.familyMemberId 
      : (inv.familyMemberId as any)?._id || (inv.familyMemberId as any)?.id;
    return invMemberId === memberId;
  });
  const summary = calculateInvestmentSummary(memberInvestments);
  
  // Group investments by type
  const investmentsByType: Record<InvestmentType, InvestmentFolio[]> = {
    'Mutual Funds': [],
    'SGB': [],
    'Digital Gold': [],
    'Indian Stocks': [],
    'US Stocks': [],
    'Corporate Bonds': [],
    'Government Bonds': [],
    'Fixed Deposits (FD)': [],
    'Bank Savings Account': [],
    'Properties': [],
    'Pension Fund (PF)': [],
    'National Pension Scheme (NPS)': [],
    'Sukanya Samriddhi Yojana (SSY)': [],
  };

  memberInvestments.forEach(investment => {
    investmentsByType[investment.type].push(investment);
  });

  return {
    familyMember,
    investmentsByType,
    ...summary,
  };
};

export const formatCurrency = (amount: number, currency: string = 'INR'): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatPercentage = (percentage: number): string => {
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
};

export const getInvestmentTypeColor = (type: InvestmentType): string => {
  const colors: Record<InvestmentType, string> = {
    'Mutual Funds': '#3B82F6',
    'SGB': '#F59E0B',
    'Digital Gold': '#EAB308',
    'Indian Stocks': '#10B981',
    'US Stocks': '#8B5CF6',
    'Corporate Bonds': '#EC4899',
    'Government Bonds': '#A855F7',
    'Fixed Deposits (FD)': '#14B8A6',
    'Bank Savings Account': '#22C55E',
    'Properties': '#DC2626',
    'Pension Fund (PF)': '#06B6D4',
    'National Pension Scheme (NPS)': '#84CC16',
    'Sukanya Samriddhi Yojana (SSY)': '#F97316',
  };
  return colors[type];
};

export const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

export const validateInvestmentForm = (form: any): string[] => {
  const errors: string[] = [];
  
  if (!form.name?.trim()) {
    errors.push('Investment name is required');
  }
  
  if (!form.familyMemberId) {
    errors.push('Family member is required');
  }
  
  if (!form.type) {
    errors.push('Investment type is required');
  }
  
  const investedAmount = parseFloat(form.investedAmount);
  if (isNaN(investedAmount) || investedAmount <= 0) {
    errors.push('Invested amount must be a positive number');
  }
  
  const currentValue = parseFloat(form.currentValue);
  if (isNaN(currentValue) || currentValue < 0) {
    errors.push('Current value must be a non-negative number');
  }
  
  if (!form.purchaseDate) {
    errors.push('Purchase date is required');
  }
  
  return errors;
};

export const validateFamilyMemberForm = (form: any): string[] => {
  const errors: string[] = [];
  
  if (!form.name?.trim()) {
    errors.push('Name is required');
  }
  
  if (!form.relationship?.trim()) {
    errors.push('Relationship is required');
  }
  
  return errors;
};
